/**
 * Panic Mode System
 * Tracks fast responses and activates panic mode with speed/score multipliers
 */

export interface PanicState {
  active: boolean;
  streak: number; // Current consecutive fast responses
  speedMultiplier: number;
  scoreMultiplier: number;
}

export class PanicModeSystem {
  private state: PanicState = {
    active: false,
    streak: 0,
    speedMultiplier: 1.0,
    scoreMultiplier: 1.0
  };

  private config: {
    triggerThreshold: number;
    speedMultiplier: number;
    scoreMultiplier: number;
    minStreak: number;
  };

  constructor(
    triggerThreshold = 0.5,
    speedMultiplier = 1.3,
    scoreMultiplier = 1.5,
    minStreak = 3
  ) {
    this.config = {
      triggerThreshold,
      speedMultiplier,
      scoreMultiplier,
      minStreak
    };
  }

  /**
   * Check if response was fast enough to count towards panic mode
   * @param responseTime Actual response time in ms
   * @param availableTime Maximum time available for this input (e.g., sequence show duration)
   * @returns true if response was fast
   */
  recordResponse(responseTime: number, availableTime: number): boolean {
    const threshold = availableTime * this.config.triggerThreshold;
    const isFast = responseTime <= threshold;

    if (isFast) {
      this.state.streak++;
      
      // Activate panic mode if streak threshold reached
      if (this.state.streak >= this.config.minStreak && !this.state.active) {
        this.activate();
      }
    } else {
      // Break streak and deactivate panic mode
      this.state.streak = 0;
      if (this.state.active) {
        this.deactivate();
      }
    }

    return isFast;
  }

  /**
   * Activate panic mode
   */
  private activate(): void {
    this.state.active = true;
    this.state.speedMultiplier = this.config.speedMultiplier;
    this.state.scoreMultiplier = this.config.scoreMultiplier;
  }

  /**
   * Deactivate panic mode
   */
  private deactivate(): void {
    this.state.active = false;
    this.state.speedMultiplier = 1.0;
    this.state.scoreMultiplier = 1.0;
  }

  /**
   * Force deactivation (on game over, restart, etc.)
   */
  reset(): void {
    this.state = {
      active: false,
      streak: 0,
      speedMultiplier: 1.0,
      scoreMultiplier: 1.0
    };
  }

  /**
   * Get current panic state
   */
  getState(): PanicState {
    return { ...this.state };
  }

  /**
   * Check if panic mode is currently active
   */
  isActive(): boolean {
    return this.state.active;
  }

  /**
   * Get current speed multiplier
   */
  getSpeedMultiplier(): number {
    return this.state.speedMultiplier;
  }

  /**
   * Get current score multiplier
   */
  getScoreMultiplier(): number {
    return this.state.scoreMultiplier;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<typeof this.config>): void {
    this.config = { ...this.config, ...config };
  }
}
